import type { StrictOmit } from '@frontend/duck-tape';
import type { ReactNode } from '@frontend/react';
import type { ButtonProps, ButtonVariant } from '../Buttons';
import { Button } from '../Buttons';
import { XStack, YStack } from '../Containers';
import { Divider } from '../Divider';
import type { IconProps } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { Text } from '../Texts/Text';
import { Modal } from './Modal';
import type { CommonModalProps } from './types';

type ConfirmationVariant = ButtonVariant;

export type ConfirmationModalProps = CommonModalProps & {
  buttonsOrientation?: 'horizontal' | 'vertical';
  cancelButtonProps?: StrictOmit<ButtonProps, 'onClick' | 'variant'>;
  cancelLabel?: string;
  hideBodyTitle?: boolean;
  iconColor?: IconProps['color'];
  iconName?: IconProps['name'];
  iconProps?: StrictOmit<IconProps, 'name'>;
  isMandatory?: boolean;
  isSubmitLoading?: boolean;
  onClickCancel?: () => void;
  onClickSubmit: () => void;
  showHeaderTitle?: boolean;
  submitButtonProps?: StrictOmit<ButtonProps, 'onClick' | 'variant'>;
  submitLabel?: string;
  subtitle: ReactNode;
  title: string;
  variant?: ConfirmationVariant;
};
export const ConfirmationModal = ({
  buttonsOrientation = 'vertical',
  cancelButtonProps,
  cancelLabel = 'Cancel',
  hideBodyTitle = false,
  iconName,
  iconProps,
  isMandatory,
  isOpen,
  isSubmitLoading,
  onClickCancel,
  onClickSubmit,
  onClose,
  showHeaderTitle = false,
  submitButtonProps,
  submitLabel = 'Submit',
  subtitle,
  title,
  variant = 'primary',
}: ConfirmationModalProps) => {
  return (
    <Modal
      Footer={
        <YStack flex={1}>
          <Divider />
          {buttonsOrientation === 'horizontal' ? (
            <XStack className="w-full gap-md p-md">
              {isMandatory ? null : onClickCancel ? (
                <YStack className="flex-1">
                  <Button
                    fullWidth
                    label={cancelLabel}
                    onClick={onClickCancel}
                    variant="secondary"
                    {...cancelButtonProps}
                  />
                </YStack>
              ) : null}
              <YStack className="flex-1">
                <Button
                  fullWidth
                  isLoading={isSubmitLoading}
                  label={submitLabel}
                  onClick={onClickSubmit}
                  variant={variant}
                  {...submitButtonProps}
                />
              </YStack>
            </XStack>
          ) : (
            <YStack gapY="md" p="md">
              <Button
                fullWidth
                isLoading={isSubmitLoading}
                label={submitLabel}
                onClick={onClickSubmit}
                variant={variant}
                {...submitButtonProps}
              />
              {isMandatory ? null : onClickCancel ? (
                <Button
                  fullWidth
                  label={cancelLabel}
                  onClick={onClickCancel}
                  variant="secondary"
                  {...cancelButtonProps}
                />
              ) : null}
            </YStack>
          )}
        </YStack>
      }
      Header={
        <YStack>
          {isMandatory ? null : showHeaderTitle ? (
            <XStack alignItems="center" className="w-full px-md py-md">
              <Text className="flex-1 text-center" type="h4">
                {title}
              </Text>
              <Icon accessibilityLabel="Close confirmation modal" name="IconX" onClick={onClose} />
            </XStack>
          ) : (
            <XStack justifyContent="end" p="md">
              <Icon accessibilityLabel="Close confirmation modal" name="IconX" onClick={onClose} />
            </XStack>
          )}
          <Divider />
        </YStack>
      }
      autoFocus={false}
      isForced={isMandatory}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      subtitle={subtitle}
      title={title}
    >
      <YStack className="gap-lg">
        <XStack className="gap-md">
          {iconName ? <Icon name={iconName} size={40} {...iconProps} /> : null}
          <YStack>
            {hideBodyTitle ? null : <Text type="h3">{title}</Text>}
            <Text color="secondary" type="p-sm">
              {subtitle}
            </Text>
          </YStack>
        </XStack>
      </YStack>
    </Modal>
  );
};
